import { ComponentType } from 'react'
import {
    AdManageIcon,
    AutomationsIcon,
    DashboardIcon,
    IconProps,
    SettingsIcon,
} from '../../icons/icons'
import { shouldShowRecipesNavItem } from '../../util/account-gates'

// A nav item is just data. No switch statements, no separate route maps —
// each item carries its own destination. Active state is derived from the
// router at render time.
export interface NavItem {
    label: string
    to: string
    visible?: (ctx: NavVisibilityContext) => boolean
    // Optional override for "is this item active?". By default an item is
    // active if pathname === to or pathname starts with to + '/'. Some items
    // need wider/narrower matchers (e.g. /dashboard shouldn't activate for /dashboards).
    match?: (pathname: string) => boolean
}

export interface NavVisibilityContext {
    isAdmin: boolean
    user: Models.User | null | undefined
}

export interface NavSection {
    // Unique key, also used to persist collapsed state in localStorage.
    key: string
    // Human label shown above the items. null = no header (e.g. for the
    // single "History" item).
    label: string | null
    // Section-level icon (kept for future use — header chips, etc).
    icon: ComponentType<IconProps>
    // Render only if predicate passes. Used to gate Admin behind is_admin.
    visible?: (ctx: NavVisibilityContext) => boolean
    items: NavItem[]
}

// Helper: exact match or startsWith(to + '/')
const startsWith = (to: string) => (pathname: string) =>
    pathname === to || pathname.startsWith(to + '/')

// Exact match only — useful when one route is a prefix of another.
const exact = (to: string) => (pathname: string) => pathname === to

export const sections: NavSection[] = [
    {
        key: 'overview',
        label: 'Overview',
        icon: DashboardIcon,
        items: [
            { label: 'Dashboard', to: '/dashboard', match: exact('/dashboard') },
            { label: 'All accounts', to: '/ad-accounts-overview' },
            { label: 'History', to: '/combined-history' },
        ],
    },
    {
        key: 'admin',
        label: 'Admin',
        icon: SettingsIcon,
        visible: ({ isAdmin }) => isAdmin,
        items: [
            { label: 'Users', to: '/admin/users' },
            { label: 'Public recipes', to: '/admin/public-recipes' },
            { label: 'Bulk actions manager', to: '/admin/bulk-actions' },
            { label: 'Recipes manager', to: '/admin/recipes' },
            { label: 'Updates', to: '/admin/updates' },
        ],
    },
    {
        key: 'automate',
        label: 'Automate',
        icon: AutomationsIcon,
        items: [
            { label: 'Segments', to: '/segments' },
            {
                label: 'Recipes',
                to: '/recipes',
                visible: ({ user }) => shouldShowRecipesNavItem(user),
            },
            { label: 'Smart campaigns', to: '/smart-campaigns' },
            { label: 'N-grams', to: '/tools/ngrams' },
        ],
    },
    {
        key: 'manage',
        label: 'Manage',
        icon: AdManageIcon,
        items: [
            { label: 'Campaigns', to: '/campaigns' },
            { label: 'Portfolios', to: '/portfolios' },
            { label: 'Ad groups', to: '/ad-groups' },
            { label: 'Ads', to: '/product-ads' },
            { label: 'Keywords', to: '/keywords' },
            { label: 'Targets', to: '/targets' },
            { label: 'Search terms', to: '/search-terms' },
            { label: 'Products', to: '/products' },
            { label: 'Placements', to: '/placements' },
        ],
    },
]

export const settingsSections: NavSection[] = [
    {
        key: 'settings',
        label: null,
        icon: SettingsIcon,
        items: [
            { label: 'Ad Account Settings', to: '/profile-settings' },
            { label: 'Data Connections', to: '/settings', match: exact('/settings') },
            { label: 'Amazon Ads Connection', to: '/keys' },
            { label: 'API Keys', to: '/api-keys' },
            { label: 'Billing', to: '/billing' },
        ],
    },
]

export const isItemActive = (item: NavItem, pathname: string): boolean => {
    if (item.match) return item.match(pathname)
    return startsWith(item.to)(pathname)
}

export const isSectionActive = (section: NavSection, pathname: string): boolean =>
    section.items.some((item) => isItemActive(item, pathname))
